// LT-135 — Accounts list page. Search by name; "New account" modal
// with name (required) + domain + industry fields. Row-click nav to
// detail page.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Plus, Search, X } from 'lucide-react';

import PageLoader from '../../components/PageLoader';
import PageShell from '../../components/PageShell';
import { DataTable, EmptyState, type ColumnDef } from '../../ui';
import { accountsAPI } from '../../services/accountsAPI';
import type { Account, AccountCreate } from '../../types/contact';

const AccountsPage: React.FC = () => {
  const navigate = useNavigate();

  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [query, setQuery] = useState('');

  const [showCreate, setShowCreate] = useState(false);

  const fetchAccounts = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const rows = await accountsAPI.list({ q: query.trim() || undefined, limit: 500 });
      setAccounts(rows);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to load accounts');
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => {
    fetchAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onCreated = async (created: Account) => {
    setShowCreate(false);
    // Navigate to the detail view of the newly-created account per HLD §3.7.
    navigate(`/accounts/${created.id}`);
  };

  const columns = useMemo<ColumnDef<Account>[]>(
    () => [
      {
        id: 'name',
        header: 'Name',
        sortable: true,
        sortValue: (row) => row.name.toLowerCase(),
        accessor: (row) => (
          <span className="font-medium text-slate-50">{row.name}</span>
        ),
      },
      {
        id: 'domain',
        header: 'Domain',
        sortable: true,
        sortValue: (row) => (row.domain ?? '').toLowerCase(),
        accessor: (row) => (
          <span className="text-slate-300">{row.domain || '—'}</span>
        ),
      },
      {
        id: 'industry',
        header: 'Industry',
        sortable: true,
        sortValue: (row) => (row.industry ?? '').toLowerCase(),
        accessor: (row) => (
          <span className="text-slate-300">{row.industry || '—'}</span>
        ),
      },
      {
        id: 'created_at',
        header: 'Created',
        sortable: true,
        sortValue: (row) => new Date(row.created_at).getTime(),
        accessor: (row) => (
          <span className="text-slate-400 font-mono text-xs">
            {new Date(row.created_at).toLocaleDateString()}
          </span>
        ),
      },
    ],
    [],
  );

  return (
    <PageShell className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-semibold text-white">Accounts</h1>
        <button
          onClick={() => setShowCreate(true)}
          className="inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg px-3 py-2 text-sm font-medium transition-colors"
        >
          <Plus size={14} /> New account
        </button>
      </div>

      {err && (
        <div className="mb-3 text-sm text-red-300 border border-red-500/30 rounded-lg px-3 py-2 bg-red-500/10">
          {err}
        </div>
      )}

      <div className="bg-gradient-to-br from-slate-800/40 to-slate-900/60 border border-white/10 rounded-xl p-3 mb-3 flex items-center gap-2">
        <div className="flex items-center border border-white/10 bg-gray-800 rounded-lg px-2 py-1 flex-grow max-w-md">
          <Search size={14} className="text-gray-400" />
          <input
            type="text"
            placeholder="Search by name"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') fetchAccounts(); }}
            className="outline-none px-2 py-1 text-sm w-full bg-transparent text-white placeholder:text-gray-400"
          />
        </div>
        <button
          onClick={fetchAccounts}
          className="px-3 py-2 rounded-lg bg-cyan-500 hover:bg-cyan-600 text-white text-sm font-medium transition-colors"
        >
          Search
        </button>
      </div>

      {loading ? (
        <PageLoader />
      ) : accounts.length === 0 ? (
        <EmptyState
          surface="accounts_list"
          icon={Building2}
          headline="No accounts yet"
          subhead="Group contacts by company so calls, deals, and activity aggregate at the account level. Add an account manually or let CSV imports match them up."
          primary={{ label: 'New account', icon: Plus, onClick: () => setShowCreate(true) }}
        />
      ) : (
        <DataTable<Account>
          data={accounts}
          columns={columns}
          getRowKey={(a) => a.id}
          onRowClick={(a) => navigate(`/accounts/${a.id}`)}
          ariaLabel="Accounts"
        />
      )}

      {showCreate && (
        <CreateAccountModal onClose={() => setShowCreate(false)} onCreated={onCreated} />
      )}
    </PageShell>
  );
};

// ---- Create modal ----------------------------------------------------------

interface CreateModalProps {
  onClose: () => void;
  onCreated: (a: Account) => void;
}

const CreateAccountModal: React.FC<CreateModalProps> = ({ onClose, onCreated }) => {
  const [name, setName] = useState('');
  const [domain, setDomain] = useState('');
  const [industry, setIndustry] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async () => {
    if (!name.trim()) {
      setErr('Name is required.');
      return;
    }
    setSaving(true);
    setErr(null);
    try {
      const payload: AccountCreate = {
        name: name.trim(),
        domain: domain.trim() || undefined,
        industry: industry.trim() || undefined,
      };
      const created = await accountsAPI.create(payload);
      onCreated(created);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Create failed');
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-gray-900 border border-white/10 rounded-xl shadow-xl w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <h2 className="text-lg font-semibold text-white">New account</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors"><X size={16} /></button>
        </div>
        <div className="p-4 space-y-3">
          {err && <div className="text-sm text-red-300">{err}</div>}
          <div>
            <label className="block text-xs font-semibold text-gray-400 uppercase mb-1">Name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="border border-white/10 bg-gray-800 text-white rounded-lg px-3 py-2 text-sm w-full focus:outline-none focus:border-cyan-500"
              autoFocus
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-400 uppercase mb-1">Domain</label>
            <input
              type="text"
              value={domain}
              placeholder="acme.example"
              onChange={(e) => setDomain(e.target.value)}
              className="border border-white/10 bg-gray-800 text-white placeholder:text-gray-400 rounded-lg px-3 py-2 text-sm w-full focus:outline-none focus:border-cyan-500"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-400 uppercase mb-1">Industry</label>
            <input
              type="text"
              value={industry}
              onChange={(e) => setIndustry(e.target.value)}
              className="border border-white/10 bg-gray-800 text-white rounded-lg px-3 py-2 text-sm w-full focus:outline-none focus:border-cyan-500"
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 px-4 py-3 border-t border-white/10 bg-white/5 rounded-b-xl">
          <button onClick={onClose} disabled={saving} className="px-3 py-2 rounded-lg text-sm text-gray-300 hover:text-white hover:bg-white/10 transition-colors">
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={saving || !name.trim()}
            className="px-3 py-2 rounded-lg bg-cyan-500 hover:bg-cyan-600 text-white text-sm font-medium transition-colors disabled:opacity-50 disabled:hover:bg-cyan-500"
          >
            Create
          </button>
        </div>
      </div>
    </div>
  );
};

export default AccountsPage;
